<template>
    <div
        :id="id"
        ref="container"
        :data-testid="testId || 'inputtextarea-wrapper'"
        class="tl-textarea-wrapper"
    >
        <FloatLabel
            :id="textareaId"
            :label="label"
        >
            <textarea
                :id="textareaId"
                ref="textarea"
                :value="model"
                :disabled="disabled"
                :class="{
                    'tl-textarea': true,
                    'tl-textarea-filled': filled,
                    'tl-textarea-disabled': disabled,
                    'tl-textarea-focus': focused || parentFocused,
                    'tl-textarea-invalid': !!error,
                }"
                :aria-invalid="!!error || undefined"
                :placeholder="placeholder"
                @input="handleInput($event)"
                @focus="focused = true"
                @blur="focused = false"
                @keydown="handleKey"
            />

            <div
                v-if="showCounter"
                class="tl-textarea-counter"
            >
                {{ model?.length ?? 0 }}
            </div>
        </FloatLabel>

        <ErrorText
            v-if="error"
            :error="error"
        />
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, useId } from 'vue'
import ErrorText from 'widget-components/components/ErrorText/ErrorText.vue'
import { isNotNil } from 'ramda'
import FloatLabel from '../FloatLabel/FloatLabel.vue'
import type { InputTextareaProps } from './types'
import { autoFocusOnMounted } from 'widget-components/integrations/CustomPage/autofocus'

defineOptions({ inheritAttrs: false })

const props = withDefaults(defineProps<InputTextareaProps & {
    placeholder?: string
    showCounter?: boolean
}>(), {
    showCounter: false,
})

const textareaId = computed(() => {
    return props.id ? `${props.id}-textarea` : useId()
})

const emit = defineEmits(['update:modelValue', 'submit', 'mounted'])

const textarea = ref<HTMLTextAreaElement>()

const model = defineModel<string>()

const filled = computed(() => !!model.value)
const focused = ref(false)
const label = computed(() => props.label)

autoFocusOnMounted(
    props,
    () => textarea.value,
)

const handleInput = (event: Event) => {
    const val = (event.target as HTMLTextAreaElement).value
    model.value = val
}

onMounted(() => {
    if (isNotNil(props.initialValue)) {
        model.value = props.initialValue
    }

    emit('mounted', textarea.value)
})

const handleKey = (event: KeyboardEvent) => {
    const code = event.code

    // ENTER
    if (code === 'Enter') {
        emit('submit')
    }
}
</script>

<style scoped lang="sass">
@import './InputTextarea'
</style>
